/**
 * Validate extracted parameters against ParamDef constraints.
 *
 * Checks:
 *   - Type correctness (string, number, boolean, WorkflowType)
 *   - Min/max constraints
 *   - Pattern matching (regex)
 *   - Options validation (enum membership)
 *   - Required field presence
 *
 * @module gibbs/text-to-action/validateParams
 */

import { WorkflowTypes } from '../registry/index';

/** Set of known WorkflowType values for quick lookup */
const WORKFLOW_TYPE_VALUES = new Set(Object.values(WorkflowTypes));

/** Primitive types that can be directly checked with typeof */
const PRIMITIVE_TYPES = new Set(['string', 'number', 'boolean', 'object']);

// ─── Single-param validation ───────────────────────────────────────────────

/**
 * Validate a single parameter value against its ParamDef.
 *
 * @param {string} name - Parameter name (for error messages)
 * @param {*} value - The value to validate
 * @param {import('../registry/schema.js').ParamDef} paramDef
 * @returns {{ errors: string[], coercedValue: * }}
 */
function validateSingleParam(name, value, paramDef) {
    const errors = [];
    const coercedValue = value;

    // --- Type check ---
    const expectedType = paramDef.type;

    if (PRIMITIVE_TYPES.has(expectedType)) {
        // For primitive types, check typeof (allow objects for compound refs etc.)
        if (expectedType === 'number' && typeof value !== 'number') {
            errors.push(`${name}: expected number, got ${typeof value} (${JSON.stringify(value)})`);
        } else if (expectedType === 'boolean' && typeof value !== 'boolean') {
            errors.push(`${name}: expected boolean, got ${typeof value} (${JSON.stringify(value)})`);
        } else if (expectedType === 'string' && typeof value !== 'string') {
            errors.push(`${name}: expected string, got ${typeof value} (${JSON.stringify(value)})`);
        }
        // 'object' type -- just needs to not be a primitive (or null)
    } else if (WORKFLOW_TYPE_VALUES.has(expectedType)) {
        // WorkflowType params accept objects, special ref strings, or step refs.
        // Loose validation -- the workflow engine enforces structure.
    }
    // Unknown types pass through without type-level validation

    // --- Options validation (enum) ---
    if (paramDef.options && paramDef.options.length > 0) {
        if (typeof value === 'string' && !paramDef.options.includes(value)) {
            errors.push(
                `${name}: "${value}" is not a valid option. Valid options: ${paramDef.options.join(', ')}`
            );
        }
    }

    // --- Constraints ---
    const constraints = paramDef.constraints;
    if (constraints && typeof value === 'number') {
        if (constraints.min != null && value < constraints.min) {
            errors.push(`${name}: ${value} is below minimum ${constraints.min}`);
        }
        if (constraints.max != null && value > constraints.max) {
            errors.push(`${name}: ${value} exceeds maximum ${constraints.max}`);
        }
    }

    if (constraints?.pattern && typeof value === 'string') {
        try {
            const regex = new RegExp(constraints.pattern);
            if (!regex.test(value)) {
                errors.push(`${name}: "${value}" does not match pattern /${constraints.pattern}/`);
            }
        } catch {
            // Invalid regex in the constraint definition -- skip pattern check
            errors.push(`${name}: invalid constraint pattern /${constraints.pattern}/`);
        }
    }

    return { errors, coercedValue };
}

// ─── Main export ───────────────────────────────────────────────────────────

/**
 * Validate extracted parameters against an ActionDef's constraints.
 *
 * @param {Object} params - Extracted parameter object (from extractParams)
 * @param {import('../registry/schema.js').ActionDef} actionDef - The target action definition
 * @returns {{ valid: boolean, errors: string[], coerced: Object }}
 */
export function validateParams(params, actionDef) {
    const errors = [];
    const coerced = { ...params };

    if (!actionDef) {
        return { valid: false, errors: ['No action definition provided'], coerced };
    }

    const allParamDefs = [...(actionDef.inputs || []), ...(actionDef.parameters || [])];

    for (const paramDef of allParamDefs) {
        const { name, required } = paramDef;
        const value = params[name];

        // --- Required check ---
        if (required && (value === undefined || value === null)) {
            errors.push(`Missing required parameter: ${name} -- ${paramDef.description || ''}`);
            continue;
        }

        // Skip validation for absent optional params
        if (value === undefined || value === null) {
            continue;
        }

        // --- Validate the value ---
        const result = validateSingleParam(name, value, paramDef);
        errors.push(...result.errors);
        coerced[name] = result.coercedValue;
    }

    return {
        valid: errors.length === 0,
        errors,
        coerced,
    };
}
